import {
    AbstractDeviceConsumablePricingItem,
    AbstractDevicePricingItem,
    Article,
    ConsumablePricingItem,
    DeviceConsumablePricingItem,
    DeviceConsumableWithSuppPricingItem,
    DevicePricingItem,
    EnumMergeQuantityMode,
    OptionPricingItem,
    PackPricingItem,
    PricingItem
} from 'quote-domain-core';
import { ArticleService } from '../article/article-service';
import { singleton } from 'tsyringe';

@singleton()
export class PricingItemQuantityService {
    constructor(private readonly articleService: ArticleService) {
    }

    public mergeQuantityByArticleCode(pricingItem: PricingItem, quantityMap: Map<any, number>): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.mergeQuantity(pricingItem, quantityMap, pricingItem.device, pricingItem.userInputs.quantity, EnumMergeQuantityMode.BY_ARTICLE);
            this.mergeQuantity(pricingItem, quantityMap, pricingItem.consumable, pricingItem.userInputs.consumableQuantity, EnumMergeQuantityMode.BY_ARTICLE);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.mergeQuantity(pricingItem, quantityMap, pricingItem.device, pricingItem.userInputs?.quantity, EnumMergeQuantityMode.BY_ARTICLE);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.mergeQuantity(pricingItem, quantityMap, pricingItem.consumable, pricingItem.userInputs.consumableQuantity, EnumMergeQuantityMode.BY_ARTICLE);
        } else if (pricingItem instanceof OptionPricingItem) {
            this.mergeQuantity(pricingItem, quantityMap, pricingItem.option, pricingItem.userInputs?.quantity, EnumMergeQuantityMode.BY_ARTICLE);
        }
    }

    public updateTotalQuantityGroupedByArticleCode(pricingItem: PricingItem, quantityMap: Map<any, number>): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            const deviceKey = this.getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem, pricingItem.device, EnumMergeQuantityMode.BY_ARTICLE);
            this.articleService.updateTotalQuantity(pricingItem.device, quantityMap.get(deviceKey));
            const consumableKey = this.getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem, pricingItem.consumable, EnumMergeQuantityMode.BY_ARTICLE);
            this.articleService.updateTotalQuantity(pricingItem.consumable, quantityMap.get(consumableKey));
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            const deviceKey = this.getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem, pricingItem.device, EnumMergeQuantityMode.BY_ARTICLE);
            this.articleService.updateTotalQuantity(pricingItem.device, quantityMap.get(deviceKey));
        } else if (pricingItem instanceof ConsumablePricingItem) {
            const consumableKey = this.getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem, pricingItem.consumable, EnumMergeQuantityMode.BY_ARTICLE);
            this.articleService.updateTotalQuantity(pricingItem.consumable, quantityMap.get(consumableKey));
        } else if (pricingItem instanceof OptionPricingItem) {
            const optionKey = this.getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem, pricingItem.option, EnumMergeQuantityMode.BY_ARTICLE);
            this.articleService.updateTotalQuantity(pricingItem.option, quantityMap.get(optionKey));
        }
    }

    public fillTotalQuantityWithBaseQuantity(pricingItem: PricingItem): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.updateTotalQuantity(pricingItem.device, pricingItem.userInputs.quantity);
            this.articleService.updateTotalQuantity(pricingItem.consumable, pricingItem.userInputs.consumableQuantity);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.articleService.updateTotalQuantity(pricingItem.device, pricingItem.userInputs.quantity);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            if (pricingItem.consumable) this.articleService.updateTotalQuantity(pricingItem.consumable, pricingItem.userInputs?.consumableQuantity);
        } else if (pricingItem instanceof OptionPricingItem) {
            this.articleService.updateTotalQuantity(pricingItem.option, pricingItem.userInputs.quantity);
        }
    }

    public buildGroupedQuantityKey(pricingItem: PricingItem): any {
        if (pricingItem instanceof DevicePricingItem) {
            return {
                key: this.getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem, pricingItem.device, EnumMergeQuantityMode.BY_GROUPING_CODE),
                quantity: pricingItem.userInputs['quantity'] ?? 0,
                hasGroupedOrCmc: this.isNumberOfChangeGroupedOrIsCMC(pricingItem)
            };
        }
        return {};
    }

    public updateTotalQuantityGroupedByGroupingCode(pricingItem: DevicePricingItem, quantityMap: Map<any, number>): void {
        let newQuantity = 0;
        if (this.isNumberOfChangeGroupedOrIsCMC(pricingItem)) {
            newQuantity = pricingItem.userInputs.quantity;
        } else {
            const deviceKey = this.getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem, pricingItem.device, EnumMergeQuantityMode.BY_GROUPING_CODE);
            newQuantity = quantityMap.get(deviceKey);
        }
        this.articleService.updateTotalQuantity(pricingItem.device, newQuantity);
    }

    public getQuantitiesOnlyForDevicesAndPack(pricingItem: PricingItem): number {
        if (pricingItem.userInputs.quantity) {
            if (pricingItem instanceof DevicePricingItem || pricingItem instanceof PackPricingItem
                || pricingItem instanceof DeviceConsumablePricingItem || pricingItem instanceof DeviceConsumableWithSuppPricingItem) {
                return Number(pricingItem.userInputs?.quantity);
            }
        }
        return 0;
    }

    public isNumberOfChangeGroupedOrIsCMC(pricingItem: PricingItem): boolean {
        return pricingItem.userInputs['numberOfChange']?.key.toLowerCase() == "groupé" || pricingItem.isCmc;
    }

    public getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem: PricingItem, article: Article, mergeQuantityMode: EnumMergeQuantityMode = EnumMergeQuantityMode.BY_ARTICLE): string {
        return JSON.stringify({
            codeElis: mergeQuantityMode == EnumMergeQuantityMode.BY_ARTICLE ? article.codeElis : article.groupingCode,
            billingMode: pricingItem.userInputs?.billingMode,
            frequency: pricingItem.userInputs?.frequency,
            isCmc: pricingItem.isCmc,
        });
    }

    private mergeQuantity(pricingItem: PricingItem, quantityMap: Map<any, number>, article: Article, quantity: string, mergeQuantityMode: EnumMergeQuantityMode = EnumMergeQuantityMode.BY_ARTICLE): void {
        const key = this.getUniqueKeyFromArticleAndUserInputsAndIsCmc(pricingItem, article, mergeQuantityMode);
        if (!quantityMap.has(key)) {
            quantityMap.set(key, parseInt(quantity ? quantity : '0'));
        } else {
            quantityMap.set(key, quantityMap.get(key) + parseInt(quantity ? quantity : '0'));
        }
    }
}
